import { createFileRoute, redirect } from '@tanstack/react-router'
import { productVersion } from '@/lib/queries/product-versions'
import { findCachedProduct, product } from '@/lib/queries/products'
import { decodeId, encodeId } from '@/lib/short-uuid'
import { ProductDetailPage } from '@/features/workspaces/stp/product-detail'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId/sketch-to-product/$productId'
)({
  params: {
    parse: (rawParams: { productId: string }) => {
      try {
        return { productId: decodeId(rawParams.productId) }
      } catch {
        return { productId: rawParams.productId }
      }
    },
    stringify: (params: { productId: string }) => ({
      productId: encodeId(params.productId),
    }),
  },
  loader: async ({ context, params }) => {
    const { queryClient } = context

    try {
      // Check infinite cache first (for optimistic updates)
      const cachedData = findCachedProduct(
        queryClient,
        params.workspaceId,
        params.productId
      )

      // ALWAYS ensure the query is properly initialized with its queryFn
      // This is critical for invalidateQueries to work later
      const queryOptions = product.findOne(params.productId)

      // Only seed if no data exists yet (don't overwrite fresher data from realtime refetch)
      if (cachedData && !queryClient.getQueryData(queryOptions.queryKey)) {
        queryClient.setQueryData(queryOptions.queryKey, cachedData)
      }

      const data = await queryClient.query({
        ...queryOptions,
        staleTime: 'static',
      })

      if (!data || data.workspace_id !== params.workspaceId) {
        throw new Error('Invalid product')
      }

      // Pre-fetch version history
      await queryClient.query({
        ...productVersion.findManyByProduct(params.productId),
        staleTime: 'static',
      })
    } catch {
      throw redirect({
        to: '/org/$orgId/workspace/$workspaceId/sketch-to-product',
        params: { orgId: params.orgId, workspaceId: params.workspaceId },
        replace: true,
      })
    }
  },
  component: ProductDetailPage,
})
